import Icon, {
  DownOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  WhatsAppOutlined,
  XOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Button, Dropdown } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import config from '../../../config';
import { Facebook, RssFeed } from '../../../utils/ExtraSvg';
import { trackEvent } from '../../../controllers/TrackingController';
import { useGetMe } from '../../hooks/query/user';
import { useGetShow } from '../../hooks/query/show';
import { useGetEpisode } from '../../hooks/query/episode';
import { getFeedUrl } from '../../../utils/showUtils';
import EmbedUtils from '../../../utils/EmbedUtils';
import Utils from '../../../utils/Utils';
import useMessage from '../../hooks/useMessage';

const ShareLinks = ({ isOpen }) => {
  const { user, isUserLoading } = useGetMe();
  const { show, isShowLoading } = useGetShow();
  const { episode, isEpisodeLoading } = useGetEpisode();
  const { message } = useMessage();

  const copyToClipboard = useCallback(
    async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        message.success({
          content: (
            <FormattedMessage defaultMessage="The link has just been copied to your clipboard!" />
          ),
        });
      } catch (error) {
        message.danger({
          content: (
            <FormattedMessage defaultMessage="Oups, we can't copy this link to your clipboard" />
          ),
        });
      }
    },
    [message]
  );

  const episodeUrl = episode ? `/episodes/${episode?.alias || episode?._id}` : '';
  const token = show?.isPrivate ? `?token=${show?.accessToken}` : '';
  const baseUrl = show?.domain
    ? `http://${show?.domain}`
    : `${config.showsUrl}/${show?.alias || show?._id}`;
  const linkToShare = `${baseUrl}${episodeUrl}${token}`;
  const isBlackListed = config.usersBlacklist ? config.usersBlacklist[user?.id] : false;

  const downloadLink = EmbedUtils.getDownloadUrl({ show, episode });
  const episodeId = episode?._id;
  const title = episode?.title || show?.title;
  const rssFeedURL = getFeedUrl(show);

  const items = useMemo(
    () =>
      [
        {
          key: 'facebook',
          icon: <Icon component={Facebook} />,
          label: (
            <a
              href={
                'http://www.facebook.com/sharer/sharer.php' +
                Utils.getQueryString({
                  u: linkToShare,
                })
              }
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent({ category: 'action-links', action: 'click', label: 'facebook' })
              }
            >
              <span>Facebook</span>
            </a>
          ),
        },
        {
          key: 'X',
          icon: <Icon component={XOutlined} />,
          label: (
            <a
              href={
                'http://x.com/intent/tweet' +
                Utils.getQueryString({
                  url: linkToShare,
                  via: isBlackListed ? null : 'acast',
                  text: title,
                })
              }
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent({ category: 'action-links', action: 'click', label: 'twitter' })
              }
            >
              <span>X</span>
            </a>
          ),
        },
        {
          key: 'whatsapp',
          icon: <Icon component={WhatsAppOutlined} />,
          label: (
            <a
              href={
                'https://wa.me/' +
                Utils.getQueryString({
                  text: linkToShare,
                })
              }
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent({ category: 'action-links', action: 'click', label: 'whatsapp' })
              }
            >
              <span>WhatsApp</span>
            </a>
          ),
        },
        {
          key: 'linkedin',
          icon: <Icon component={LinkedinOutlined} />,
          label: (
            <a
              href={
                'https://www.linkedin.com/sharing/share-offsite/' +
                Utils.getQueryString({
                  url: linkToShare,
                })
              }
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent({ category: 'action-links', action: 'click', label: 'linkedin' })
              }
            >
              <span>LinkedIn</span>
            </a>
          ),
        },
        {
          key: 'divider',
          type: 'divider',
        },
        {
          key: 'rss',
          icon: <Icon component={RssFeed} />,
          label: (
            <a href={rssFeedURL} target="_blank" rel="noopener noreferrer">
              <FormattedMessage defaultMessage="RSS Feed" />
            </a>
          ),
        },
        {
          key: 'website',
          icon: <GlobalOutlined />,
          label: (
            <a
              title={title}
              href={linkToShare}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent({ category: 'action-links', action: 'click', label: 'website' })
              }
            >
              <FormattedMessage defaultMessage="Website" />
            </a>
          ),
        },
        {
          key: 'divider-2',
          type: 'divider',
          isDisabled: !(downloadLink || episodeId),
        },
        {
          key: 'download',
          icon: <Icon component={CopyOutlined} />,
          label: (
            <a
              href={downloadLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                copyToClipboard(downloadLink);
                trackEvent({
                  category: 'action-links',
                  action: 'click',
                  label: 'download-link',
                });
              }}
            >
              <FormattedMessage defaultMessage="Copy Download Link" />
            </a>
          ),
          isDisabled: !downloadLink,
        },
        {
          key: 'copyEpisodeId',
          icon: <Icon component={CopyOutlined} />,
          label: (
            <a
              href={'#'}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                copyToClipboard(episodeId);
                trackEvent({
                  category: 'action-links',
                  action: 'click',
                  label: 'episode-id',
                });
              }}
            >
              <FormattedMessage defaultMessage="Copy Episode Id" />
            </a>
          ),
          isDisabled: !episodeId,
        },
      ]
        .filter((item) => !item.isDisabled)
        .map((item) => {
          delete item.isDisabled;
          return {
            ...item,
          };
        }),
    [copyToClipboard, downloadLink, isBlackListed, linkToShare, rssFeedURL, title, episodeId]
  );

  const openProps = isOpen ? { open: true } : {};

  return isShowLoading || isEpisodeLoading || isUserLoading ? (
    <Button loading>
      <FormattedMessage defaultMessage="Share Links" />
      <DownOutlined />
    </Button>
  ) : (
    <Dropdown menu={{ items }} {...openProps}>
      <Button>
        <FormattedMessage defaultMessage="Share Links" />
        <DownOutlined />
      </Button>
    </Dropdown>
  );
};

ShareLinks.propTypes = {
  isOpen: PropTypes.bool,
};

export default ShareLinks;
